import type { ReactNode } from "react";

export type AdminAlertKind = "success" | "error";

const ICON: Record<AdminAlertKind, string> = {
  success: "✓", // ✓
  error: "✕", // ✕
};

/**
 * Inline action-feedback banner for the admin views. Replaces the faint
 * `launchpad__hint` paragraph that rendered success/error messages as light-gray
 * centered text (easy to miss inside a form). Dark text on a tinted background
 * with a colored left bar + icon gives a quick success/error signal.
 *
 * Accessibility: `role="alert"` (assertive) for errors so screen readers
 * announce failures immediately; `role="status"` (polite) for success so it does
 * not interrupt. See `docs/ui-styleguide.md` — this is the standard admin
 * action-feedback pattern; reuse it instead of bespoke `launchpad__hint` markup.
 */
export function AdminAlert({
  kind,
  children,
}: {
  kind: AdminAlertKind;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className={`admin-alert admin-alert--${kind}`}
      role={kind === "error" ? "alert" : "status"}
    >
      <span className="admin-alert__icon" aria-hidden="true">
        {ICON[kind]}
      </span>
      <span className="admin-alert__body">{children}</span>
    </div>
  );
}
